import { useCallback, useEffect, useState } from "react";
import {
  Route,
  Routes,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Button, LoadingSkeleton, MenuButton, Row } from "@narmi/design_system";
import { Dialog, Drawer } from "cerulean";
import { entities, modules } from "byzantine";
import PreviewCard from "../../PreviewCard/PreviewCard";
import TemplateForm from "../../../src/modules/transfers/wire/templates/TemplatesList/TemplateDrawer/TemplateForm/TemplateForm";
import RecipientForm from "../../../src/modules/transfers/wire/templates/TemplatesList/TemplateDrawer/RecipientForm/RecipientForm";
import { DOMESTIC_WIRE_TEMPLATES_PATH, TEMPLATES_ROUTE } from "../constants";
import styles from "../InternationalWireTemplates/InternationalWireTemplatesContainer.module.scss";

const BASE_PATH = `/${TEMPLATES_ROUTE}/${DOMESTIC_WIRE_TEMPLATES_PATH}`;

const DRAWER_PATTERNS = [
  `${BASE_PATH}/new`,
  `${BASE_PATH}/edit/:templateId`,
  `${BASE_PATH}/recipients/*`,
];

const TemplateCard = ({
  template,
  recipient,
  onEdit,
  showActions,
}: {
  template: API.FedwireTemplate;
  recipient: API.WireRecipient;
  onEdit: () => void;
  showActions: boolean;
}) => {
  const { l10n } = useLocalization();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { deleteTemplate, loading } =
    entities.fedwireTemplates.useDeleteTemplateRequest();
  const destinationAccountPreview =
    modules.wireTemplates.getDestinationAccountPreview(recipient);

  return (
    <>
      <PreviewCard
        header={template.name}
        subHeader={recipient.name}
        iconName="narmi-icon-bank"
        iconContent={destinationAccountPreview}
        actionButton={
          showActions ? (
            <MenuButton label={l10n.getString("aria-label-template-menu")}>
              <MenuButton.Item
                label={l10n.getString("button-edit-template")}
                startIcon="edit-2"
                onSelect={onEdit}
              />
              <MenuButton.Item
                label={l10n.getString("button-delete-template")}
                startIcon="trash-2"
                onSelect={() => setIsDeleteDialogOpen(true)}
              />
            </MenuButton>
          ) : undefined
        }
      />
      <Dialog
        isOpen={isDeleteDialogOpen}
        onUserDismiss={() => setIsDeleteDialogOpen(false)}
        title={l10n.getString("dialog-confirm-delete-template", {
          templateName: template.name,
        })}
        width="385px"
        footer={
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                onClick={() => setIsDeleteDialogOpen(false)}
                kind="negative"
                label={l10n.getString("button-cancel")}
                disabled={loading}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                onClick={() => deleteTemplate(template.id)}
                kind="primary"
                label={l10n.getString("button-confirm-delete-template")}
                isLoading={loading}
                disabled={loading}
              />
            </Row.Item>
          </Row>
        }
      >
        <div style={{ overflowWrap: "anywhere" }}>
          {l10n.getString("domestic-wire-templates-confirm-delete", {
            templateName: template.name,
          })}
        </div>
      </Dialog>
    </>
  );
};

const DomesticWireTemplatesContainer = () => {
  const { l10n } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, load } = modules.wireTemplates.bootstrap.useBootstrap();
  const { templatesWithRecipients } = modules.wireTemplates.useData();
  const { showManageTemplatesUI } = modules.wireTemplates.useWirePermissions();

  useEffect(() => {
    load();
  }, [load]);

  const isDrawerOpen = DRAWER_PATTERNS.some((pattern) =>
    Boolean(matchPath(pattern, location.pathname))
  );

  const closeDrawer = useCallback(
    () => navigate(`${BASE_PATH}${location.search}`),
    [navigate, location.search]
  );

  const addRecipient = useCallback(
    () =>
      navigate(`${BASE_PATH}/recipients/new${location.search}`, {
        state: { from: location.pathname },
      }),
    [navigate, location.pathname, location.search]
  );

  if (loading) {
    return (
      <div className="margin--top--l">
        <LoadingSkeleton isLoading lines={4} />
      </div>
    );
  }

  return (
    <>
      {showManageTemplatesUI && (
        <Row justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="plain"
              startIcon="plus"
              onClick={() => navigate(`${BASE_PATH}/new${location.search}`)}
              label={l10n.getString("new-template")}
            />
          </Row.Item>
        </Row>
      )}
      {templatesWithRecipients.length === 0 ? (
        <p className="margin--top--l">
          {l10n.getString("domestic-wire-templates-empty")}
        </p>
      ) : (
        <div className={`margin--top--l ${styles.grid}`}>
          {templatesWithRecipients.map(({ template, recipient }) => (
            <TemplateCard
              key={template.id}
              template={template}
              recipient={recipient}
              showActions={showManageTemplatesUI}
              onEdit={() => navigate(`${BASE_PATH}/edit/${template.id}${location.search}`)}
            />
          ))}
        </div>
      )}
      {showManageTemplatesUI && (
        <Drawer
          isOpen={isDrawerOpen}
          paddingSize="none"
          onUserDismiss={closeDrawer}
          showControls={false}
          depth="633px"
        >
          <modules.wireTemplates.upsertTemplateForm.Provider>
            <Routes>
              <Route
                path="new"
                element={
                  <TemplateForm
                    onClose={closeDrawer}
                    onAddRecipient={addRecipient}
                  />
                }
              />
              <Route
                path="edit/:templateId"
                element={
                  <TemplateForm
                    onClose={closeDrawer}
                    onAddRecipient={addRecipient}
                  />
                }
              />
              <Route path="recipients/new" element={<RecipientForm />} />
            </Routes>
          </modules.wireTemplates.upsertTemplateForm.Provider>
        </Drawer>
      )}
    </>
  );
};

export default DomesticWireTemplatesContainer;
